import { AppState, Platform } from 'react-native';
import { registerFcmToken } from '../api/client';
import { getFirebaseWebConfig } from '../config/firebaseWeb';
import { loadFirebaseWebSdk } from './firebaseWebSdk';
import { tr } from '../i18n/runtime';
import {
  shouldSuppressIncomingCallPush,
  tryClaimMessageNotify,
} from './pushDedupe';
import { isChatMuted } from '../screens/chatNotificationPrefsStorage';

function logPush(message: string, meta?: Record<string, unknown>) {
  if (!__DEV__) return;
  if (meta) {
    console.info(`[push] ${message}`, meta);
    return;
  }
  console.info(`[push] ${message}`);
}

let webPushTeardown: (() => void) | undefined;

/** Same asset as `public/favicon.png` (synced from assets/icon.png in writeFirebaseWebSw.mjs). */
const WEB_NOTIFICATION_ICON = '/favicon.png';

async function teardownWebPush(): Promise<void> {
  webPushTeardown?.();
  webPushTeardown = undefined;
}

async function syncFcmWebToBackend(): Promise<() => void> {
  await teardownWebPush();
  const cfg = getFirebaseWebConfig();
  if (!cfg) {
    // Web push optional in dev; avoid console noise until EXPO_PUBLIC_FIREBASE_WEB_* are set.
    return () => {};
  }
  if (typeof globalThis.window === 'undefined' || typeof navigator === 'undefined') {
    return () => {};
  }
  try {
    const sdk = await loadFirebaseWebSdk();
    if (!sdk) return () => {};
    const { initializeApp, getApps, getMessaging, getToken, isSupported, onMessage } = sdk;
    if (!(await isSupported())) {
      logPush('FCM web: messaging not supported in this browser');
      return () => {};
    }
    // Разрешение здесь только читается, а не спрашивается (аудит 2026-10-04, п. 15): запрос
    // без нажатия браузеры глушат и запоминают как отказ. Спрашивает кнопка «Включить
    // уведомления» (requestNotifyPermission), и она же после согласия зовёт эту функцию снова.
    const perm = typeof Notification !== 'undefined' ? Notification.permission : 'denied';
    if (perm !== 'granted') {
      logPush('FCM web: notification permission not granted');
      return () => {};
    }

    const app =
      getApps().length === 0
        ? initializeApp({
            apiKey: cfg.apiKey,
            authDomain: cfg.authDomain,
            projectId: cfg.projectId,
            storageBucket: cfg.storageBucket,
            messagingSenderId: cfg.messagingSenderId,
            appId: cfg.appId,
            ...(cfg.measurementId ? { measurementId: cfg.measurementId } : {}),
          })
        : getApps()[0]!;

    const messaging = getMessaging(app);

    if (!('serviceWorker' in navigator)) {
      return () => {};
    }

    const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js', {
      scope: '/',
    });
    await navigator.serviceWorker.ready;

    const token = await getToken(messaging, {
      vapidKey: cfg.vapidKey,
      serviceWorkerRegistration: registration,
    });
    if (token) {
      await registerFcmToken(token, 'web');
      logPush('FCM web token registered');
    }

    const unsubMsg = onMessage(messaging, (payload) => {
      void (async () => {
      if (typeof globalThis.Notification === 'undefined') return;
      if (Notification.permission !== 'granted') return;
      const data = (payload.data || {}) as Record<string, string | undefined>;
      const t = data.type;
      const muted = typeof data.chatId === 'string' ? await isChatMuted(data.chatId) : false;
      if (t === 'message' && typeof data.messageId === 'string') {
        if (muted) return;
        if (AppState.currentState === 'active' && !tryClaimMessageNotify(data.messageId)) {
          return;
        }
        const title = payload.notification?.title || data.title || 'vr msgr';
        const fromData =
          typeof data.messagePreview === 'string' && data.messagePreview.trim().length > 0
            ? data.messagePreview.trim()
            : '';
        const body = fromData || payload.notification?.body || tr('notifyBodyMessage');
        try {
          new Notification(title, {
            body,
            icon: WEB_NOTIFICATION_ICON,
            tag: typeof data.chatId === 'string' ? data.chatId : undefined,
          });
        } catch {
          /* ignore */
        }
      } else if (t === 'contact_request') {
        // Заявка в друзья: на web системного слоя для неё нет, показываем обычное
        // уведомление — иначе о заявке вообще ничего не сообщает (стори 5.7).
        const title = payload.notification?.title || tr('contactRequestIncomingTitle');
        const body =
          typeof data.requesterName === 'string' && data.requesterName.trim().length > 0
            ? data.requesterName.trim()
            : payload.notification?.body || '';
        try {
          new Notification(title, { body, icon: WEB_NOTIFICATION_ICON, tag: 'contact-request' });
        } catch {
          /* ignore */
        }
      } else if (t === 'comment_reply') {
        // Ответ на комментарий в канале: системного слоя для него в браузере нет, как и у
        // заявки в друзья ниже. Заглушённый канал молчит — тем же признаком, что и сообщения.
        if (muted) return;
        const title = data.title || payload.notification?.title || tr('notifyCommentReplyTitle');
        const body = data.body || payload.notification?.body || '';
        // Тег по посту: ответы в разных обсуждениях не затирают друг друга, а несколько
        // ответов в одном схлопываются в последний — открыв ветку, человек увидит все.
        const tag = data.postId ? `comment-reply:${data.postId}` : 'comment-reply';
        try {
          new Notification(title, { body, icon: WEB_NOTIFICATION_ICON, tag });
        } catch {
          /* ignore */
        }
      } else if (t === 'reminder' || t === 'birthday' || t === 'scheduled_failed') {
        // Напоминания и дни рождения: на web системного слоя для них нет, как и у заявки
        // в друзья выше. Тег — тип уведомления, чтобы напоминание не затирало поздравление.
        const title =
          payload.notification?.title ||
          data.title ||
          (t === 'birthday' ? tr('notifyBirthdayTitle') : tr('notifyReminderTitle'));
        const body = data.body || payload.notification?.body || '';
        try {
          new Notification(title, { body, icon: WEB_NOTIFICATION_ICON, tag: t });
        } catch {
          /* ignore */
        }
      } else if (t === 'call' && typeof data.chatId === 'string') {
        if (muted) return;
        if (shouldSuppressIncomingCallPush(data.chatId)) return;
        const title =
          typeof data.title === 'string' ? data.title : payload.notification?.title || 'Call';
        const body =
          typeof data.callerName === 'string'
            ? data.callerName
            : payload.notification?.body || '';
        try {
          new Notification(title, { body, icon: WEB_NOTIFICATION_ICON, tag: data.chatId });
        } catch {
          /* ignore */
        }
      }
      })();
    });

    webPushTeardown = () => {
      unsubMsg();
      webPushTeardown = undefined;
    };

    return () => {
      void teardownWebPush();
    };
  } catch (e) {
    logPush('FCM web sync failed', { err: String(e) });
    return () => {};
  }
}

/**
 * Повторная регистрация токена (например после возврата приложения на передний план).
 */
export async function refreshFcmRegistration(): Promise<void> {
  if (Platform.OS === 'web') {
    if (typeof navigator === 'undefined') return;
    const cfg = getFirebaseWebConfig();
    if (!cfg) return;
    try {
      const sdk = await loadFirebaseWebSdk();
      if (!sdk) return;
      const { initializeApp, getApps, getMessaging, getToken, isSupported } = sdk;
      if (!(await isSupported())) return;
      const app = getApps().length ? getApps()[0]! : initializeApp({
          apiKey: cfg.apiKey,
          authDomain: cfg.authDomain,
          projectId: cfg.projectId,
          storageBucket: cfg.storageBucket,
          messagingSenderId: cfg.messagingSenderId,
          appId: cfg.appId,
          ...(cfg.measurementId ? { measurementId: cfg.measurementId } : {}),
        });
      const messaging = getMessaging(app);
      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) return;
      const token = await getToken(messaging, {
        vapidKey: cfg.vapidKey,
        serviceWorkerRegistration: reg,
      });
      if (token) await registerFcmToken(token, 'web');
    } catch {
      /* noop */
    }
    return;
  }
  try {
    const mod = await import('@react-native-firebase/messaging');
    const messaging = mod.default;
    const token = await messaging().getToken();
    if (token) {
      await registerFcmToken(token, Platform.OS === 'ios' ? 'ios' : 'android');
    }
  } catch {
    /* noop */
  }
}

/**
 * FCM token → backend. Возвращает отписку (native: onTokenRefresh; web: foreground onMessage).
 */
export async function syncFcmTokenToBackend(): Promise<() => void> {
  if (Platform.OS === 'web') {
    return syncFcmWebToBackend();
  }
  try {
    const { default: messaging, AuthorizationStatus } = await import(
      '@react-native-firebase/messaging'
    );
    // Проверка, а не запрос (аудит 2026-10-04, п. 34): системное окно на входе шло подряд с
    // ещё тремя. Спрашивает карточка-пояснение после входа. Токен регистрируем и без
    // разрешения: на Android данные звонка приходят и без права показывать уведомления
    // (входящий рисуется оверлеем), а на iOS токен APNs выдаётся независимо от него.
    const authStatus = await messaging().hasPermission();
    if (authStatus !== AuthorizationStatus.AUTHORIZED && authStatus !== AuthorizationStatus.PROVISIONAL) {
      logPush('FCM permission not granted yet', { authStatus });
    }
    const token = await messaging().getToken();
    if (token) {
      const platform = Platform.OS === 'ios' ? 'ios' : 'android';
      await registerFcmToken(token, platform);
      logPush('FCM token registered', { platform });
    }
    return messaging().onTokenRefresh(async (newToken) => {
      const platform = Platform.OS === 'ios' ? 'ios' : 'android';
      await registerFcmToken(newToken, platform);
      logPush('FCM token refreshed');
    });
  } catch (e) {
    logPush('FCM sync failed', { err: String(e) });
    return () => {};
  }
}
